import React, { useState, useEffect } from 'react';
import api from '../services/api';
import FabricTypeahead from '../components/FabricTypeahead';

// Merchandising-owned Pending Fabrics queue (design ruling 9). Request-and-proceed creates
// pending fabrics; here merch APPROVES, RENAMES, or MERGES the near-duplicates it breeds.
// Oldest-first with an age column so staleness is the first thing visible (the general-store
// -indent 13-day shape). Gate is server-side (FABRIC_CURATE = admin/management/merchandiser).
const wrap = { padding: 24, maxWidth: 1100, margin: '0 auto' };
const th = { textAlign: 'left', fontSize: 10, textTransform: 'uppercase', color: '#888', fontWeight: 700, padding: '8px 10px', borderBottom: '2px solid #eee' };
const td = { padding: '10px', borderBottom: '1px solid #f2f2f2', fontSize: 13, verticalAlign: 'top' };
const btn = (bg, fg) => ({ background: bg, color: fg, border: 'none', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginRight: 6 });
const input = { padding: '5px 7px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };

function ageBadge(days) {
  const d = Number(days) || 0;
  const bg = d >= 7 ? '#FEE2E2' : d >= 3 ? '#FEF3C7' : '#ECFDF5';
  const fg = d >= 7 ? '#B91C1C' : d >= 3 ? '#B45309' : '#047857';
  return <span style={{ background: bg, color: fg, borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 700 }}>{d}d</span>;
}

export default function PendingFabrics() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);   // {id, fabric_name, fabric_code, fabric_type, composition, gsm, dia_width}
  const [merging, setMerging] = useState(null);    // {id, survivor_id, survivor_name}
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/oe/fabric-master/pending').then(r => setRows(r.data.data || [])).catch(e => setErr(e.response?.data?.error || 'Failed to load'));
  useEffect(() => { load(); }, []);

  const approve = (id) => { setBusy(true); api.patch(`/oe/fabric-master/${id}/approve`).then(load).catch(e => window.alert(e.response?.data?.error || 'Failed')).finally(() => setBusy(false)); };

  const saveEdit = () => {
    if (!editing.fabric_name || !editing.fabric_name.trim()) return window.alert('Name is required');
    setBusy(true);
    api.patch(`/oe/fabric-master/${editing.id}?approve=1`, {
      fabric_name: editing.fabric_name, fabric_code: editing.fabric_code || null, fabric_type: editing.fabric_type || null,
      composition: editing.composition || null, gsm: editing.gsm || null, dia_width: editing.dia_width || null,
    }).then(() => { setEditing(null); load(); }).catch(e => window.alert(e.response?.data?.error || 'Failed')).finally(() => setBusy(false));
  };

  const doMerge = () => {
    if (!merging.survivor_id) return window.alert('Pick a survivor fabric to merge into');
    if (!window.confirm(`Merge this pending fabric into “${merging.survivor_name}”? All records re-point to the survivor. This cannot be undone.`)) return;
    setBusy(true);
    api.post(`/oe/fabric-master/${merging.id}/merge`, { survivor_id: merging.survivor_id })
      .then(() => { setMerging(null); load(); }).catch(e => window.alert(e.response?.data?.error || 'Failed')).finally(() => setBusy(false));
  };

  return (
    <div style={wrap}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Pending Fabrics</h2>
      <p style={{ color: '#777', fontSize: 13, marginTop: 0 }}>
        Fabrics created on-the-fly while raising GPOs or Deroi estimates. Approve, rename, or merge duplicates into an existing quality. Oldest first — clear the ≥7-day rows.
      </p>
      {err && <div style={{ color: '#A32D2D', marginBottom: 10 }}>{err}</div>}
      {!rows.length && <div style={{ color: '#999', padding: 24, textAlign: 'center', border: '1px dashed #ddd', borderRadius: 8 }}>No pending fabrics. 🎉</div>}
      {rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 8, overflow: 'hidden' }}>
          <thead><tr>
            <th style={th}>Age</th><th style={th}>Fabric</th><th style={th}>Details</th><th style={th}>Requested by</th><th style={{ ...th, width: 260 }}>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map(f => (
              <tr key={f.id}>
                <td style={td}>{ageBadge(f.age_days)}</td>
                <td style={td}>
                  {editing && editing.id === f.id ? (
                    <div style={{ display: 'grid', gap: 4 }}>
                      <input style={{ ...input, width: '100%' }} value={editing.fabric_name} placeholder="Canonical name"
                        onChange={e => setEditing({ ...editing, fabric_name: e.target.value })} />
                      <input style={{ ...input, width: '100%' }} value={editing.fabric_code || ''} placeholder="Fabric code (e.g. FAB-INTFOMA-270)"
                        onChange={e => setEditing({ ...editing, fabric_code: e.target.value })} />
                      <input style={{ ...input, width: '100%' }} value={editing.fabric_type || ''} placeholder="Type (interlock / pique / rib …)"
                        onChange={e => setEditing({ ...editing, fabric_type: e.target.value })} />
                    </div>
                  ) : (
                    <span style={{ fontWeight: 600 }}>{f.fabric_name}</span>
                  )}
                </td>
                <td style={td}>
                  <span style={{ color: '#666' }}>
                    {[f.composition, f.gsm && `${f.gsm} gsm`, f.dia_width].filter(Boolean).join(' · ') || '—'}
                  </span>
                </td>
                <td style={td}>{f.requested_by_name || '—'}</td>
                <td style={td}>
                  {merging && merging.id === f.id ? (
                    <div style={{ display: 'grid', gap: 6 }}>
                      <FabricTypeahead value={merging.survivor_id} initialLabel={merging.survivor_name}
                        placeholder="Merge into…"
                        onChange={(id, obj) => setMerging({ ...merging, survivor_id: id, survivor_name: obj ? obj.fabric_name : '' })} />
                      <div>
                        <button disabled={busy} style={btn('#7C3AED', '#fff')} onClick={doMerge}>Confirm merge</button>
                        <button style={btn('#eee', '#333')} onClick={() => setMerging(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : editing && editing.id === f.id ? (
                    <div>
                      <button disabled={busy} style={btn('#16a34a', '#fff')} onClick={saveEdit}>Save &amp; approve</button>
                      <button style={btn('#eee', '#333')} onClick={() => setEditing(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div>
                      <button disabled={busy} style={btn('#16a34a', '#fff')} onClick={() => approve(f.id)}>Approve</button>
                      <button style={btn('#EEF2FF', '#3B3A8F')} onClick={() => setEditing({ id: f.id, fabric_name: f.fabric_name, fabric_code: f.fabric_code, fabric_type: f.fabric_type, composition: f.composition, gsm: f.gsm, dia_width: f.dia_width })}>Rename</button>
                      <button style={btn('#F3E8FF', '#7C3AED')} onClick={() => setMerging({ id: f.id, survivor_id: '', survivor_name: '' })}>Merge</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
